import { memo, useMemo } from "react";
import styled, { css } from "styled-components";
import {
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BoltIcon,
  CalendarIcon,
  CheckIcon,
  ClockIcon,
  DocumentPlusIcon,
  FlagIcon,
  ForwardIcon,
  GlobeAltIcon,
  HandRaisedIcon,
  InformationCircleIcon,
  LockClosedIcon,
  NoSymbolIcon,
  PresentationChartLineIcon,
  SquaresPlusIcon,
} from "@heroicons/react/24/outline";
import {
  Amount as AmountBase,
  Chain,
  Detail,
} from "@sablier/v2-components/molecules";
import { StreamStatus } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import type { IChainDisplay } from "@sablier/v2-types";
import type { ComponentProps, PropsWithChildren } from "react";

const DetailAdvanced = styled(Detail)`
  *[data-component="frame"],
  *[data-component="label"] {
    color: ${(props) => props.theme.colors.primaryMiddle};
  }
`;

const DetailCliff = styled(Detail)<{ isHighlighted: boolean }>`
  ${(props) =>
    props.isHighlighted &&
    css`
      *[data-component="frame"],
      *[data-component="label"],
      *[data-component="value"] {
        color: ${(props) => props.theme.colors.yellow};
      }
    `}
`;

const DetailDuration = styled(Detail)`
  *[data-component="frame"],
  *[data-component="label"] {
    color: ${(props) => props.theme.colors.gray100};
  }

  *[data-component="value"] {
    color: ${(props) => props.theme.colors.gray200};
  }
`;

const DetailEnsurance = styled(Detail)`
  *[data-component="frame"],
  *[data-component="label"] {
    color: ${(props) => props.theme.colors.white};
  }
`;

const DetailLoading = styled(Detail)`
  *[data-component="frame"] {
    background-color: ${(props) => props.theme.colors.dark300};
  }
`;

interface Props extends PropsWithChildren<unknown> {
  value?: string;
  toPage?: string;
}

interface AmountProps extends Omit<Props, "value"> {
  label?: string;
  value?: ComponentProps<typeof AmountBase>;
}

interface NetworkProps extends Omit<Props, "value"> {
  value?: IChainDisplay;
}

interface TimelineProps extends Props {
  status?: StreamStatus;
  label?: string;
}

interface EnsuranceProps extends Props {
  label?: string;
}
interface DurationProps extends Props {
  label?: string;
}

function Advanced(props: Props) {
  const { t } = useT();
  return (
    <DetailAdvanced
      icon={ArrowTrendingUpIcon}
      label={t("structs.sablierDynamicStream")}
      {...props}
    />
  );
}

function Amount({ value, label, toPage }: AmountProps) {
  const { t } = useT();
  return (
    <Detail
      icon={BanknotesIcon}
      label={label || t("words.amount")}
      toPage={toPage}
    >
      <AmountBase {...value} />
    </Detail>
  );
}

function Cancelable(props: Props) {
  const { t } = useT();
  return (
    <Detail icon={HandRaisedIcon} label={t("words.cancelability")} {...props} />
  );
}

function Cliff({ isCliffing, ...props }: Props & { isCliffing?: boolean }) {
  const { t } = useT();
  return (
    <DetailCliff
      isHighlighted={!!isCliffing}
      icon={isCliffing ? LockClosedIcon : ClockIcon}
      label={`${t("words.cliff")}${
        isCliffing ? ` (${_.capitalize(t("words.ongoing"))})` : ""
      }`}
      {...props}
    />
  );
}

function Duration(durationProps: DurationProps) {
  const { t } = useT();
  const { label = t("structs.durationDetailsLabel"), ...props } = durationProps;
  return <DetailDuration icon={CalendarIcon} label={label} {...props} />;
}

function Ensurance(ensuranceProps: EnsuranceProps) {
  const { t } = useT();
  const { label = t("structs.ensurance"), ...props } = ensuranceProps;
  return <DetailEnsurance icon={FlagIcon} label={label} {...props} />;
}

function Info(infoProps: EnsuranceProps) {
  const { t } = useT();
  const { label = t("structs.more"), ...props } = infoProps;
  return (
    <DetailEnsurance icon={InformationCircleIcon} label={label} {...props} />
  );
}

function CreatedOn(props: Props) {
  const { t } = useT();
  return (
    <Detail icon={DocumentPlusIcon} label={t("structs.created")} {...props} />
  );
}

function StartedOn(props: Props) {
  const { t } = useT();
  return (
    <Detail icon={DocumentPlusIcon} label={t("structs.started")} {...props} />
  );
}

function Group(props: Props) {
  const { t } = useT();
  return <Detail icon={SquaresPlusIcon} label={t("words.group")} {...props} />;
}

function Network({ value, ...props }: NetworkProps) {
  const { t } = useT();
  return (
    <Detail icon={GlobeAltIcon} label={t("words.chain")} {...props}>
      <Chain
        empty={"dark300"}
        image={value?.image}
        size={18}
        name={value?.name || ""}
      />
    </Detail>
  );
}

function Shape(props: Props) {
  const { t } = useT();
  return (
    <Detail
      icon={PresentationChartLineIcon}
      label={`${t("words.shape")} (${t("words.lockup")})`}
      {...props}
    />
  );
}

function Status(props: Props) {
  const { t } = useT();
  return <Detail icon={BoltIcon} label={t("words.status")} {...props} />;
}

function Timeline({
  status = StreamStatus.STREAMING,
  label,
  ...props
}: TimelineProps) {
  const labelIcon = useMemo(() => {
    switch (status) {
      case StreamStatus.PENDING:
        return ForwardIcon;
      case StreamStatus.SETTLED:
      case StreamStatus.DEPLETED_SETTLED:
        return CheckIcon;
      case StreamStatus.CANCELED:
      case StreamStatus.DEPLETED_CANCELED:
        return NoSymbolIcon;
      case StreamStatus.STREAMING:
      default:
        return undefined;
    }
  }, [status]);

  return (
    <Detail
      icon={CalendarIcon}
      label={label}
      labelIcon={labelIcon}
      {...props}
    />
  );
}

function Loading({ className }: { className?: string }) {
  return <DetailLoading className={className} isLoading />;
}

const Details = {
  Advanced: memo(Advanced),
  Amount: memo(Amount),
  Cancelable: memo(Cancelable),
  Cliff: memo(Cliff),
  CreatedOn: memo(CreatedOn),
  StartedOn: memo(StartedOn),
  Ensurance: memo(Ensurance),
  Group: memo(Group),
  Loading: memo(Loading),
  Info: memo(Info),
  Network: memo(Network),
  Shape: memo(Shape),
  Status: memo(Status),
  Timeline: memo(Timeline),
  Duration: memo(Duration),
};

export default Details;
